import type { Profile, StaffRole, User } from "@amazing-care/types";
import type { AuthAdapter } from "../types";

const MOCK_USER: User = {
  id: "00000000-0000-0000-0000-000000000001",
  email: "jordan.lee@amazingcare.test",
  account_id: null,
  created_at: new Date().toISOString(),
};

const MOCK_NAME = "Jordan Lee";

interface MockAdapterOptions {
  /** Role the mock profile reports. Driven by VITE_MOCK_ROLE in the app. */
  role?: StaffRole;
  /** Start already signed in, so local development skips the login screen. */
  signedIn?: boolean;
  /** Departments the mock manager may edit. */
  managedDepartmentIds?: string[];
  /** Simulate a signed-in account that has no profile, i.e. is not a member. */
  member?: boolean;
}

export function createMockAdapter(options: MockAdapterOptions = {}): AuthAdapter {
  const role: StaffRole = options.role ?? "Admin";
  const managedDepartmentIds =
    options.managedDepartmentIds ?? (role === "Manager" ? ["clinical-ops"] : []);
  const isMember = options.member ?? true;
  let currentUser: User | null = options.signedIn ? MOCK_USER : null;
  const listeners = new Set<(user: User | null) => void>();

  function notify(user: User | null) {
    currentUser = user;
    for (const listener of listeners) listener(user);
  }

  return {
    async getUser() {
      return currentUser;
    },

    async getProfile(userId: string): Promise<Profile | null> {
      if (!currentUser || currentUser.id !== userId || !isMember) return null;
      return {
        id: MOCK_USER.id,
        email: MOCK_USER.email,
        full_name: MOCK_NAME,
        role,
        managedDepartmentIds,
      };
    },

    async signInWithEmail(email: string) {
      const user = { ...MOCK_USER, email };
      notify(user);
      return user;
    },

    async signInWithMicrosoft() {
      notify(MOCK_USER);
    },

    async signOut() {
      notify(null);
    },

    async resetPassword() {
      // No-op in mock.
    },

    async handOffSession() {
      return currentUser ? { access_token: "mock-access", refresh_token: "mock-refresh" } : null;
    },

    async setSession() {
      notify(MOCK_USER);
      return MOCK_USER;
    },

    onAuthStateChange(cb: (user: User | null) => void) {
      listeners.add(cb);
      cb(currentUser);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

/** Default instance for tests that do not care about role or session. */
export const mockAdapter: AuthAdapter = createMockAdapter();
